import React, { useCallback, useEffect, useRef, useState } from "react";
import { cx, useTranslations } from "@jugl-web/utils";
import { useFilePreview } from "@jugl-web/domain-resources/files/providers/FilePreviewProvider";
import { useDownloadManagerProvider } from "@jugl-web/domain-resources/files/providers/DownloadManagerProvider";
import { InteractiveContainer, Menu, MenuItem } from "@jugl-web/ui-components";
import { ChatMessagePayloadAttachment } from "@jugl-web/rest-api";
import { useChatMessageBubble } from "../../providers/ChatMessageBubbleProvider/useChatMessageBubble";
import { ChatMessageBubbleAction } from "../../types/ChatMessageBubbleAction";
import { ReactComponent as DownloadMenuIcon } from "./assets/download.svg";
import { ReactComponent as DeleteMenuIcon } from "./assets/delete.svg";
import { FilePreviewTile } from "./components/FilePreviewTile";

export const FileAttachment: React.FC = () => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const triggeredByContextMenu = useRef(false);
  const {
    attachments = [],
    isOutgoing,
    entityId,
    hasContentProtectionRestriction,
    isTusDownloadAvailable,
    isMessageActionsDisabled,
    availableActions,
    isPending,
    isError,
    getAttachmentUrls,
    onDeleteAttachment,
    isAttachmentOpeningDisabled,
  } = useChatMessageBubble();

  const { previewFiles, previewFile, closePreview, currentIndex } =
    useFilePreview();
  const { download, files } = useDownloadManagerProvider();
  const { t } = useTranslations();

  const createPreviewObject = useCallback(
    (attachment: ChatMessagePayloadAttachment, isMultiple?: boolean) => {
      const { stream, preview } = getAttachmentUrls(attachment);

      return {
        url: stream,
        preview,
        name:
          attachment.name ||
          t({
            id: "chats-page.chat-file",
            defaultMessage: "Chat File",
          }),
        mimeType: attachment.mimetype,
        onDownload: !hasContentProtectionRestriction
          ? () => window.open(stream, "_blank")
          : undefined,
        onDelete:
          isMultiple &&
          onDeleteAttachment &&
          availableActions?.includes(ChatMessageBubbleAction.EDIT)
            ? () => onDeleteAttachment(attachment.uid)
            : undefined,
        onPreviewClose: () => setIsPreviewOpen(false),
      };
    },
    [
      availableActions,
      getAttachmentUrls,
      hasContentProtectionRestriction,
      onDeleteAttachment,
      t,
    ]
  );

  const handlePreview = useCallback(
    (index: number) => {
      const attachment = attachments[index];
      const previewData = createPreviewObject(attachment);

      setIsPreviewOpen(true);

      if (attachments.length === 1) {
        previewFile(previewData);
      } else {
        const previewFilesData = attachments.map((file) =>
          createPreviewObject(file, true)
        );
        previewFiles(previewFilesData, previewData.url);
      }
    },
    [attachments, createPreviewObject, previewFile, previewFiles]
  );

  useEffect(() => {
    if (isPreviewOpen) {
      if (!!attachments.length && typeof currentIndex === "number") {
        if (attachments[currentIndex]) {
          handlePreview(currentIndex);
          return;
        }
        handlePreview(attachments.length - 1);
      } else {
        closePreview();
      }
    }
  }, [isPreviewOpen, currentIndex, handlePreview, attachments, closePreview]);
  const handleDownload = (attachment: ChatMessagePayloadAttachment) => {
    download(attachment.uid, {
      entityId,
      fileSize: attachment.size,
      mimeType: attachment.mimetype,
      module: "attachments",
      fileName: attachment.name || "jugl-file",
      path: `${entityId}/${attachment.uid}`,
    }).then((file) => {
      if (!file) return;
      const link = document.createElement("a");
      const objectUrl = URL.createObjectURL(file);
      link.href = objectUrl;
      link.download = attachment.name || "jugl-file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    });
  };

  return (
    <div className="flex min-w-[220px] flex-col gap-1">
      {attachments.map((attachment, idx) => {
        const downloadFile = files[attachment.uid];
        const isDownloading = downloadFile?.status === "in-progress";
        const isDownloadError = downloadFile?.status === "error";
        const isDownloadAvailable =
          !isPending &&
          !isError &&
          !hasContentProtectionRestriction &&
          (!downloadFile ||
            downloadFile?.status === "cancelled" ||
            isDownloadError);

        const wrapperClassName = cx(
          "flex w-full items-start gap-2 rounded-lg py-1 px-1 transition-colors",
          isOutgoing ? "hover:bg-[#3E88C3]" : "hover:bg-grey-100"
        );

        const menuItems: MenuItem[] = [];

        if (isTusDownloadAvailable && isDownloadAvailable && !isDownloading) {
          menuItems.push({
            id: "download",
            label: t({
              id: "chats-page.download-file",
              defaultMessage: "Download file",
            }),
            icon: <DownloadMenuIcon />,
            onSelect: () => handleDownload(attachment),
          });
        }

        if (
          attachments.length > 1 &&
          availableActions?.includes(ChatMessageBubbleAction.EDIT) &&
          onDeleteAttachment &&
          !isMessageActionsDisabled
        ) {
          menuItems.push({
            id: "delete",
            label: t({
              id: "chats-page.delete-file",
              defaultMessage: "Delete file",
            }),
            icon: <DeleteMenuIcon />,
            onSelect: () => onDeleteAttachment(attachment.uid),
          });
        }

        if (!menuItems.length) {
          return (
            <InteractiveContainer
              key={+idx}
              isDisabled={isAttachmentOpeningDisabled || isError}
              className={cx(
                wrapperClassName,
                (isAttachmentOpeningDisabled || isError) &&
                  "pointer-events-none"
              )}
              onClick={() => handlePreview(idx)}
            >
              <FilePreviewTile
                attachment={attachment}
                isDownloadAvailable={isDownloadAvailable}
                onDownload={handleDownload}
              />
            </InteractiveContainer>
          );
        }

        return (
          <Menu
            key={+idx}
            hasBackdrop
            placement="bottom-end"
            autoClose
            sections={[menuItems]}
            renderTrigger={({ Trigger, triggerRef, isOpen }) => (
              <Trigger
                as={InteractiveContainer}
                isDisabled={isPending || isError}
                className={cx(
                  wrapperClassName,
                  isOpen && isOutgoing && "bg-[#3E88C3]",
                  isOpen && !isOutgoing && "bg-grey-100",
                  (isPending || isError) && "pointer-events-none"
                )}
                ref={triggerRef}
                onPointerDown={(e) => {
                  if (triggeredByContextMenu.current) {
                    triggeredByContextMenu.current = false;
                    return;
                  }

                  // Headless UI v2 opens menus on pointerdown. Block that for regular clicks (LMB)
                  if (e.button === 0) {
                    e.preventDefault();
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  triggeredByContextMenu.current = true;
                  e.currentTarget.dispatchEvent(
                    new PointerEvent("pointerdown", {
                      bubbles: true,
                      cancelable: true,
                      button: 0,
                      pointerType: "mouse",
                    })
                  );
                }}
                onClick={(e) => {
                  if (triggeredByContextMenu.current) {
                    triggeredByContextMenu.current = false;
                    return;
                  }
                  if (isAttachmentOpeningDisabled) return;
                  e.preventDefault();
                  handlePreview(idx);
                }}
              >
                <FilePreviewTile
                  attachment={attachment}
                  isDownloadAvailable={isDownloadAvailable}
                  onDownload={handleDownload}
                />
              </Trigger>
            )}
          />
        );
      })}
    </div>
  );
};
